import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap, catchError, concatMap, takeLast, tap, exhaustMap } from 'rxjs/operators';
import { TenantPermissionAssigneeTargetLinkService, Pagination, PermissionAssigneeTargetLinkContainerModel, PermissionService, TenantPermissionAssigneeTargetService, TenantRoleContainerModel, TenantRoleService, TenantTeamRoleContainerModel, TenantTeamRoleService, TenantUserRoleContainerModel, TenantUserRoleService, ClientSidePermissionRoleLinksService, ClientSidePermissionRoleLinkContainerModel } from 'blg-akaun-ts-lib';
import { RolePermActions } from '../actions';
import { ToastrService } from 'ngx-toastr';
import { AppConfig } from '../../../../visa';
import { PermissionFacade } from '../../../../modules/permission/facades/permission.facade';

@Injectable()
export class RolePermEffects {

    private readonly apiVisa = AppConfig.apiVisa;
    private readonly masterVisa = AppConfig.masterVisa;

    addPermSetHdrToRoleInit$ = createEffect(() => this.actions$.pipe(
      ofType(RolePermActions.addPermSetHdrToRoleInit),
      map(action => {
          let dto: any;

          dto = {
              "assignee_hdr_table": "app_mst_role",
              "assignee_guid": action.roleGuid,
              "applet_guid": action.appletGuid,
              "perm_set_hdr_guids": action.permSetHdrGuids
          }

          return {
            dto: dto,
            action: action
          };
      }),
      exhaustMap(res => this.tenantPermissionAssigneeTargetLinkService.addPermSetListing(res.dto, this.apiVisa)
      .pipe(
          map(_ => {
              this.displayToasterSuccess('Permission set added successfully');
              this.viewColFacade.updateInstance(res.action.prevIndex, {
                ...res.action.prevLocalState,
                deactivateReturn: false,
                deactivateAdd: false,
                deactivateList: false,
                selectedIndex: 2
              });
              this.viewColFacade.onPrev(res.action.prevIndex);
              return RolePermActions.addPermSetHdrToRoleSuccess();
          }),
          catchError((err) => {
              this.displayToasterError(err.message);
              return of(RolePermActions.addPermSetHdrToRoleFailure({error: err.message}))
          })
      ))
  ))

    /**
     * Create new role.
     */
    createRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.createRoleInit),
        map(action => {
            const role = new TenantRoleContainerModel();
            role.app_mst_role.code = action.role.code;
            role.app_mst_role.name = action.role.name;
            role.app_mst_role.status = action.role.status;

            return role;
        }),
        mergeMap(role => this.tenantRoleService.post(role, this.apiVisa)
        .pipe(
            map(role => {
                this.displayToasterSuccess('Role created successfully');
                return RolePermActions.createRoleSuccess();
            }),
            catchError((err) => {
               //this.displayToasterError(err.message);
                this.displayToasterError(err.error.data[0].longMessage);
                return of(RolePermActions.createRoleFailure({error: err.message}));
            })
        ))
    ));

    /**
     * Update role details.
     */
     updateRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.updateRoleInit),
        concatMap(action => this.tenantRoleService.getByGuid(action.guid, this.apiVisa)
        .pipe(
            map(a => a.data),
            map(b => {
                b.app_mst_role.name = action.role.name;
                b.app_mst_role.description = action.role.description;
                b.app_mst_role.status = action.role.status;

                return b;
            })
        )),
        concatMap(c => this.tenantRoleService.put(c, this.apiVisa).pipe(
            map(_ => {
                this.displayToasterSuccess('Role updated successfully');
                return RolePermActions.updateRoleSuccess();
            }),
            catchError(err => {
                this.displayToasterError(err.message);
                return of(RolePermActions.updateRoleFailure({error: err.message}));
            })
        ))
    ));

    /**
     * Delete role and all related permission assignee target links associated with this role.
     *
     */
    deleteRoleWithPerms$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.deleteRoleWithPermsInit),
        concatMap(action => {
            return action.permAssigneeGuids;
        }),
        mergeMap(guid =>
            this.tenantPermAssigneeService.delete(guid, this.apiVisa)
            .pipe(
                takeLast(1),
                map(_ => {
                    this.displayToasterSuccess('Permission assignee target links removed successfully');
                    return RolePermActions.deleteRoleWithPermsSuccess();
                }),
                catchError((err) => {
                    this.displayToasterError(err.message);
                    return of(RolePermActions.deleteRoleWithPermsFailure({error: err.message}));
                })
            )
        ),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(RolePermActions.deleteRoleWithPermsFailure({error: err.message}));
        })
    ));

    /**
     * Delete role.
     *
     */
    deleteRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.deleteRoleInit),
        mergeMap(action => this.tenantRoleService.delete(action.guid, this.apiVisa)
        .pipe(
            map(_ => {
                this.displayToasterSuccess('Role removed successfully');
                return RolePermActions.deleteRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.deleteRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Add users to role.
     */
    addUsersToRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.addUsersToRoleInit),
        concatMap(action => {
            const arr: TenantUserRoleContainerModel[] = [];
            action.subjectGuids.forEach(guid => {
                const a = new TenantUserRoleContainerModel();
                a.link_subject_to_role.app_subject_guid = guid;
                a.link_subject_to_role.app_mst_role_guid = action.roleGuid;

                arr.push(a);
            })
            return arr
        }),
        mergeMap(obj => this.tenantUserRoleService.post(obj, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Users added successfully');
                return RolePermActions.addUsersToRoleSucess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.addUsersToRoleFailure({error: err.message}))
            })
        ))
    ));

    /**
     * Remove users from role.
     */
    removeUsersFromRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.removeUsersFromRoleInit),
        concatMap(action => {
            return action.guids
        }),
        mergeMap(guid => this.tenantUserRoleService.delete(guid, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Users removed successfully');
                return RolePermActions.removeUsersFromRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.removeUsersFromRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Add teams to role.
     */
     addTeamToRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.addTeamsToRoleInit),
        concatMap(action => {
            const arr: TenantTeamRoleContainerModel[] = [];
            action.teamGuids.forEach(guid => {
                const a = new TenantTeamRoleContainerModel();
                a.app_mst_link_grp_to_role.mst_role_guid = action.roleGuid;
                a.app_mst_link_grp_to_role.mst_grp_guid = guid;

                arr.push(a);
            });
            return arr
        }),
        mergeMap(obj => this.tenantTeamRoleService.post(obj, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Teams added successfully');
                return RolePermActions.addTeamsToRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.addTeamsToRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Remove teams from role.
     */
    removeTeamsFromRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.removeTeamsFromRoleInit),
        concatMap(action => action.guids),
        mergeMap(guid => this.tenantTeamRoleService.delete(guid, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Teams removed successfully');
                return RolePermActions.removeTeamsFromRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.removeTeamsFromRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Add permission sets to role.
     */
    addPermSetsToRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.addPermSetsToRoleInit),
        mergeMap(action => {
            const source: Observable<PermissionAssigneeTargetLinkContainerModel>[] = [];
            action.permSetLines.forEach(line => {
                const model = new PermissionAssigneeTargetLinkContainerModel();
                model.permAssigneeTargetLink.applet_hdr_guid = action.appletGuid;
                model.permAssigneeTargetLink.assignee_hdr_table = 'app_mst_role';
                model.permAssigneeTargetLink.assignee_hdr_guid = action.roleGuid;
                model.permAssigneeTargetLink.perm_set_hdr_guid = line.hdr_guid;
                model.permAssigneeTargetLink.perm_set_line_guid = line.guid;
                model.permAssigneeTargetLink.target_hdr_table = line.target_hdr_table;
                model.permAssigneeTargetLink.target_hdr_guid = line.target_hdr_guid;
                model.permAssigneeTargetLink.target_type = line.target_type;
                model.permAssigneeTargetLink.perm_dfn_code = line.code;
                model.permAssigneeTargetLink.perm_dfn_guid = null;

                const criteria = [
                    {columnName: 'perm_code', operator: '=', value: line.code.toString()}
                ]
                const pagination = new Pagination(null, null, criteria);
                const model$ = this.permService.getByCriteria(pagination, this.apiVisa)
                    .pipe(
                        map(x => {
                            model.permAssigneeTargetLink.perm_dfn_guid = x.data[0].permDfn.guid;
                            return model;
                        })
                    );
                source.push(model$)
            })
            return forkJoin(source).pipe(map(x => x));
        }),
        concatMap(models => models),
        mergeMap(obj => this.tenantPermAssigneeService.post(obj, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Permission sets added successfully');
                return RolePermActions.addPermSetsToRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.addPermSetsToRoleFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(RolePermActions.addPermSetsToRoleFailure({error: err.message}));
        })
    ))

    /**
     * Remove permission sets from role.
     */
     removePermSetsFromRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.removePermSetsFromRoleInit),
        concatMap(action => action.guids),
        mergeMap(guid => this.tenantPermAssigneeService.delete(guid, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Permission sets removed successfully');
                return RolePermActions.removePermSetsFromRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.removePermSetsFromRoleFailure({error: err.message}));
            })
        ))
    ))

    /**
     * Add client side permissions to role.
     */
    addClientSidePermsToRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.addClientSidePermsToRoleInit),
        concatMap(action => {
            const arr: ClientSidePermissionRoleLinkContainerModel[] = [];
            action.clientGuids.forEach(guid => {
                const a = new ClientSidePermissionRoleLinkContainerModel();
                //a.link_subject_to_role.app_subject_guid = guid;
                a.bl_applet_client_side_perm_to_role_link.applet_guid = action.appletGuid;
                //a.link_subject_to_role.app_mst_role_guid = action.roleGuid;
                a.bl_applet_client_side_perm_to_role_link.role_guid = action.roleGuid;
                a.bl_applet_client_side_perm_to_role_link.client_perm_guid = guid;
                arr.push(a);
            })
            return arr
        }),
        mergeMap(obj => this.tenantClientSidePermissionService.post(obj, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Client side permissions added successfully');
                return RolePermActions.addClientSidePermsToRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.addClientSidePermsToRoleFailure({error: err.message}))
            })
        ))
    ));

    /**
     * Remove client side permissions from role.
     */
    removeClientSidePermsFromRole$ = createEffect(() => this.actions$.pipe(
        ofType(RolePermActions.removeClientSidePermsFromRoleInit),
        concatMap(action => {
            return action.guids
        }),
        mergeMap(guid => this.tenantClientSidePermissionService.delete(guid, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Client side permissions removed successfully');
                return RolePermActions.removeClientSidePermsFromRoleSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(RolePermActions.removeClientSidePermsFromRoleFailure({error: err.message}));
            })
        ))
    ))

    constructor(
        private tenantPermissionAssigneeTargetLinkService: TenantPermissionAssigneeTargetLinkService,
        private tenantRoleService: TenantRoleService,
        private tenantUserRoleService: TenantUserRoleService,
        private tenantTeamRoleService: TenantTeamRoleService,
        private tenantPermAssigneeService: TenantPermissionAssigneeTargetService,
        private tenantClientSidePermissionService: ClientSidePermissionRoleLinksService,
        private permService: PermissionService,
        private actions$: Actions,
        private toaster: ToastrService,
        private viewColFacade: PermissionFacade,
    ) {};

    /**
     * Display a successful toaster message.
     *
     * @param message : the message to display
     * @returns the toaster
     */
    displayToasterSuccess(message: string) {
        return this.toaster.success(
            message,
            'Success',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

    /**
     * Display a toaster when there is an error.
     *
     * @param message : the messsage to display
     * @returns the toaster
     */
    displayToasterError(message: string) {
        return this.toaster.error(
            message,
            'Error',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

}
